
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Box, Typography, FormControl, InputLabel, Select, Slider, Stack, InputAdornment, IconButton, Paper } from '@mui/material';
import NotificationSnackbar from './NotificationSnackbar';
import Barcode from 'react-barcode';
import api from '../services/api';

// TicketPreview: Professional live preview for badge/ticket
export function TicketPreview({ design, ticket }) {
  // Deduce package/type/category
  const heading = design?.heading || ticket.packageName?.toUpperCase() || ticket.type?.toUpperCase() || 'TICKET';
  const category = ticket.type?.toUpperCase() || 'N/A';
  const packageName = ticket.packageName || '';
  const bookerName = ticket.attendee || ticket.company || ticket.name || 'N/A';
  const email = ticket.email || '';
  const peopleCount = ticket.peopleCount;
  const status = ticket.status || '';
  const barcodeValue = String(ticket.reference || ticket.email || 'TICKET');

  // Color helpers: compute contrast and lightened variants from badgeColor
  const badge = design?.badgeColor || '#0ea5a4';
  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const bigint = parseInt(full, 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
  };
  const luminance = (r, g, b) => {
    const a = [r, g, b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  };
  const getContrastColor = (hex) => {
    try {
      const [r, g, b] = hexToRgb(hex);
      return luminance(r, g, b) > 0.5 ? '#000' : '#fff';
    } catch (e) {
      return '#000';
    }
  };
  const lighten = (hex, pct) => {
    try {
      const [r, g, b] = hexToRgb(hex);
      const nr = Math.round(r + (255 - r) * pct);
      const ng = Math.round(g + (255 - g) * pct);
      const nb = Math.round(b + (255 - b) * pct);
      return `rgb(${nr}, ${ng}, ${nb})`;
    } catch (e) {
      return hex;
    }
  };
  const readableOnBadge = getContrastColor(badge);
  const subtleAccent = lighten(badge, 0.6);

  return (
    <div
      style={{
        width: 320,
        minHeight: 460,
        border: `2px solid ${design?.badgeColor || '#0ea5a4'}`,
        borderRadius: 14,
        background: design?.backgroundType === 'gradient'
          ? `linear-gradient(135deg, ${design.backgroundGradientStart}, ${design.backgroundGradientEnd})`
          : design?.backgroundImage
            ? `url(${design.backgroundImage})`
            : design?.backgroundColor || '#ffffff',
          backgroundSize: design?.backgroundImage ? (typeof design.backgroundSize === 'number' ? `${design.backgroundSize}%` : (design.backgroundSize || 'cover')) : undefined,
          backgroundPosition: design?.backgroundImage ? `${design.backgroundPositionX || 50}% ${design.backgroundPositionY || 50}%` : undefined,
        color: '#222',
        padding: 16,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        fontFamily: 'inherit',
        boxShadow: '0 4px 18px rgba(0,0,0,0.08)',
        position: 'relative',
      }}
    >
      {/* Heading and category */}
      <div style={{ fontWeight: 700, fontSize: 20, color: badge, marginBottom: 2 }}>
        {heading}
      </div>
      <div style={{ fontSize: 13, color: subtleAccent, marginBottom: 6 }}>
        Category: <b>{category}</b>
      </div>
      {/* Package name */}
      {packageName && (
        <div style={{ fontSize: 14, fontWeight: 600, color: badge, marginBottom: 6 }}>
          Package: {packageName}
        </div>
      )}
      {/* Reference/ID */}
      <div style={{
        background: '#e0e7ef',
        borderRadius: 7,
        padding: '4px 12px',
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 8,
        color: badge,
        width: '100%',
        textAlign: 'center',
      }}>
        Ticket Reference <span style={{ color: badge, fontWeight: 700 }}>{ticket.reference}</span>
      </div>
      {/* Barcode - improved sizing */}
      <div style={{ margin: '0 auto 8px auto', display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 260, width: '100%' }}>
        <div style={{ background: '#fff', padding: '8px 12px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Barcode value={barcodeValue} width={1.2} height={48} fontSize={12} displayValue={false} format="CODE128" margin={0} />
        </div>
        <div style={{ fontSize: 11, color: subtleAccent, marginTop: 8 }}>{barcodeValue}</div>
      </div>
      {/* Person/Company/Email/Count - label depends on ticket type */}
      <div style={{ width: '100%', marginBottom: 6 }}>
        {(() => {
          const t = (ticket.type || '').toLowerCase();
          if (t === 'sponsorship' || t === 'sponsor') return <div style={{ fontWeight: 700, fontSize: 14, color: subtleAccent, marginBottom: 2 }}>Sponsor/Company</div>;
          if (t === 'booth') return <div style={{ fontWeight: 700, fontSize: 14, color: subtleAccent, marginBottom: 2 }}>Booth Owner/Company</div>;
          if (t === 'speaker') return <div style={{ fontWeight: 700, fontSize: 14, color: subtleAccent, marginBottom: 2 }}>Speaker/Company</div>;
          if (t === 'exhibitor') return <div style={{ fontWeight: 700, fontSize: 14, color: subtleAccent, marginBottom: 2 }}>Exhibitor/Company</div>;
          return <div style={{ fontWeight: 700, fontSize: 14, color: subtleAccent, marginBottom: 2 }}>Attendee/Company</div>;
        })()}
        <div style={{ fontSize: 14, fontWeight: 600, color: badge, marginBottom: 2 }}>{bookerName}</div>
        <div style={{ fontSize: 12, color: subtleAccent, marginBottom: 2 }}>Email: <b style={{ color: badge }}>{email}</b></div>
        {peopleCount !== undefined && (
          <div style={{ fontSize: 12, color: subtleAccent, marginBottom: 2 }}>Booked: <b style={{ color: badge }}>{peopleCount}</b> {peopleCount === 1 ? 'person' : 'people'}</div>
        )}
      </div>
      {/* Status and package */}
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 12, marginBottom: 6 }}>
        <div>
          <div style={{ color: subtleAccent, fontWeight: 600 }}>Type</div>
          <div style={{ color: badge }}>{category}</div>
        </div>
        <div>
          <div style={{ color: subtleAccent, fontWeight: 600 }}>Status</div>
          <div style={{ color: badge }}>{status}</div>
        </div>
      </div>
      {/* Phone */}
      {ticket.phone && (
        <div style={{ fontSize: 12, color: subtleAccent, marginBottom: 2 }}>Phone: <b style={{ color: badge }}>{ticket.phone}</b></div>
      )}
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 10 }}>This is a live preview</div>
    </div>
  );
}

const defaultDesigns = {
  // Keep an internal attendee key for legacy fallbacks but hide it from the UI options
  attendee: {
    backgroundColor: '#ffffff', badgeColor: '#0ea5a4', backgroundType: 'solid', heading: 'ATTENDEE'
  },
  delegate: {
    backgroundColor: '#f0fdf4', badgeColor: '#a19d75', backgroundType: 'image', heading: 'DELEGATE', backgroundImage: ''
  },
  exhibitor: {
    backgroundColor: '#fef9c3', badgeColor: '#eab308', backgroundType: 'solid', heading: 'EXHIBITOR'
  },
  speaker: {
    backgroundColor: '#e0f2fe', badgeColor: '#0ea5e9', backgroundType: 'gradient', heading: 'SPEAKER', backgroundGradientStart: '#57636b', backgroundGradientEnd: '#111213'
  },
  booth: {
    backgroundColor: '#f8fafc', badgeColor: '#222', backgroundType: 'solid', heading: 'BOOTH'
  },
  sponsorship: {
    backgroundColor: '#fff7ed', badgeColor: '#c2410c', backgroundType: 'solid', heading: 'SPONSOR'
  }
};



// Save all category designs in one state object
export default function TicketDesignModal({ open, onClose, onSave, type = 'attendee', initialDesigns = {} }) {
  const [designs, setDesigns] = useState({ ...defaultDesigns });
  const [selectedType, setSelectedType] = useState(type);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [packageCategories, setPackageCategories] = useState([]);
  // Ensure all design fields are present for each type
  const designFields = [
    'badgeColor', 'heading', 'backgroundColor', 'backgroundType', 'backgroundGradientStart', 'backgroundGradientEnd', 'backgroundImage', 'backgroundPositionX', 'backgroundPositionY', 'backgroundSize'
  ];

  // Load saved designs and real package categories from backend on open
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    // Get ticket designs
    api.get('/api/ticket-designs').then(res => {
      if (!mounted) return;
      if (res.data && typeof res.data === 'object') {
        const merged = { ...defaultDesigns, ...res.data };
        // normalize color hex strings to 6-digit
        const normalizeHex = (v) => {
          if (!v || typeof v !== 'string') return v;
          const s = v.trim();
          if (!s.startsWith('#')) return s;
          const hex = s.slice(1);
          if (hex.length === 3) return '#' + hex.split('').map(c => c + c).join('');
          return '#' + hex;
        };
        const normalized = Object.keys(merged).reduce((acc, k) => {
          const d = { ...merged[k] };
          ['badgeColor', 'backgroundColor', 'backgroundGradientStart', 'backgroundGradientEnd'].forEach(pk => {
            if (d[pk]) d[pk] = normalizeHex(d[pk]);
          });
          acc[k] = d;
          return acc;
        }, {});
        setDesigns(normalized);
        // ensure selectedType exists
        if (!merged[selectedType]) {
          const first = Object.keys(merged)[0];
          if (first) setSelectedType(first);
        }
      } else {
        setDesigns({ ...defaultDesigns });
      }
    }).catch(() => setDesigns({ ...defaultDesigns }));
    // Get real package categories and ensure booth/sponsorship are available
    api.get('/api/packages').then(res => {
      if (!mounted) return;
      let cats = [];
      if (Array.isArray(res.data.packages)) {
        cats = [...new Set(res.data.packages.map(p => p.category))];
      } else if (Array.isArray(res.data)) {
        cats = [...new Set(res.data.map(p => p.category))];
      }
      const combined = [...new Set([...(cats || []), 'booth', 'sponsorship'])];
      setPackageCategories(combined);
      if (combined.length && !combined.includes(selectedType)) setSelectedType(combined[0]);
    }).catch(() => setPackageCategories(['booth', 'sponsorship']));
    return () => { mounted = false; };
  }, [open]);

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
  };
  // Update local design state only; saving is performed by explicit Save action
  const handleDesignChange = (e) => {
    const { name, value } = e.target;
    // normalize short hex values to 6-digit for color inputs
    const colorKeys = ['badgeColor', 'backgroundColor', 'backgroundGradientStart', 'backgroundGradientEnd'];
    const normalizeHex = (v) => {
      if (!v || typeof v !== 'string') return v;
      const s = v.trim();
      if (!s.startsWith('#')) return s;
      const hex = s.slice(1);
      if (hex.length === 3) return '#' + hex.split('').map(c => c + c).join('');
      return '#' + hex;
    };
    const newVal = colorKeys.includes(name) ? normalizeHex(value) : value;
    setDesigns(d => {
      const updated = { ...d, [selectedType]: { ...d[selectedType], [name]: newVal } };
      return updated;
    });
  };
  

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setDesigns(d => ({ ...d, [selectedType]: { ...d[selectedType], backgroundImage: url, backgroundType: url ? 'image' : d[selectedType]?.backgroundType, backgroundPositionX: 50, backgroundPositionY: 50, backgroundSize: 100 } }));
  };
  const handleSave = async () => {
    try {
      // Only send allowed fields
      const cleanDesign = {};
      designFields.forEach(f => {
        if (designs[selectedType][f] !== undefined) cleanDesign[f] = designs[selectedType][f];
      });
      // Basic validation: require heading and badgeColor before saving
      if (!cleanDesign.heading || !cleanDesign.badgeColor) {
        setSnackbar({ open: true, message: 'Please fill Heading and Badge Color before saving.', severity: 'warning' });
        return;
      }
      const res = await api.put(`/api/ticket-designs/${selectedType}`, { ...cleanDesign, __userInitiated: true });
      // If backend returned canonical category/design, use that to update local state
      if (res.data) {
        // If backend returned full designs object, use it to ensure exact persisted state
        if (res.data.designs && typeof res.data.designs === 'object') {
          setDesigns(prev => ({ ...prev, ...res.data.designs }));
        } else if (res.data.design) {
          const cat = res.data.category || selectedType;
          setDesigns(prev => ({ ...prev, [cat]: res.data.design }));
        }
        setSnackbar({ open: true, message: `Design for ${res.data.category || selectedType} saved!`, severity: 'success' });
        onSave && onSave(res.data);
      } else {
        setSnackbar({ open: true, message: `Design for ${selectedType} saved!`, severity: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save design', severity: 'error' });
    }
  };

  // No autosave timer to clear; saving is explicit

  // Preview ticket for selected type
  const previewTicket = {
    type: selectedType,
    attendee: 'John Doe',
    reference: 'TCKT-123456',
    status: 'approved',
    company: 'Acme Corp',
    email: 'john@example.com',
    phone: '+255 700 000 000',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Design {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Ticket/Badge</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ flex: '1 1 300px' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Design Options</Typography>
            <TextField select label="Type" name="type"
              value={packageCategories.length > 0 && packageCategories.includes(selectedType) ? selectedType : ''}
              onChange={handleTypeChange} fullWidth sx={{ mb: 2 }}>
              {packageCategories.length === 0 && (
                <MenuItem value="">No categories</MenuItem>
              )}
              {packageCategories.map(cat => (
                <MenuItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</MenuItem>
              ))}
            </TextField>
            <TextField label="Badge Color" name="badgeColor" value={designs[selectedType]?.badgeColor || ''} onChange={handleDesignChange} fullWidth sx={{ mb: 2 }} />
            <TextField label="Ticket Heading" name="heading" value={designs[selectedType]?.heading || ''} onChange={handleDesignChange} fullWidth sx={{ mb: 2 }} />
            <TextField label="Background Color" name="backgroundColor" value={designs[selectedType]?.backgroundColor || ''} onChange={handleDesignChange} fullWidth sx={{ mb: 2 }} />
            {/* Gradient color mixing */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Gradient Start"
                name="backgroundGradientStart"
                type="color"
                value={designs[selectedType]?.backgroundGradientStart || '#ffffff'}
                onChange={handleDesignChange}
                sx={{ width: 120 }}
              />
              <TextField
                label="Gradient End"
                name="backgroundGradientEnd"
                type="color"
                value={designs[selectedType]?.backgroundGradientEnd || '#ffffff'}
                onChange={handleDesignChange}
                sx={{ width: 120 }}
              />
              <TextField
                select
                label="Background Type"
                name="backgroundType"
                value={designs[selectedType]?.backgroundType || 'solid'}
                onChange={handleDesignChange}
                sx={{ width: 120 }}
              >
                <MenuItem value="solid">Solid</MenuItem>
                <MenuItem value="gradient">Gradient</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </TextField>
            </Box>
            {/* Image upload for ticket background */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Background Image URL"
                    name="backgroundImageUrl"
                    value={designs[selectedType]?.backgroundImage || ''}
                    onChange={handleImageUrlChange}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: designs[selectedType]?.backgroundImage ? (
                        <InputAdornment position="end"><img src={designs[selectedType].backgroundImage} alt="thumb" style={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 4 }} /></InputAdornment>
                      ) : null
                    }}
                  />
                </Stack>
                {designs[selectedType]?.backgroundImage && designs[selectedType]?.backgroundType === 'image' && (
                  <Box>
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>Image crop / position</Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="caption">X</Typography>
                        <Slider value={designs[selectedType]?.backgroundPositionX || 50} min={0} max={100} onChange={(e, val) => setDesigns(d => ({ ...d, [selectedType]: { ...d[selectedType], backgroundPositionX: Number(val) } }))} sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ width: 36, textAlign: 'right' }}>{designs[selectedType]?.backgroundPositionX || 50}%</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="caption">Y</Typography>
                        <Slider value={designs[selectedType]?.backgroundPositionY || 50} min={0} max={100} onChange={(e, val) => setDesigns(d => ({ ...d, [selectedType]: { ...d[selectedType], backgroundPositionY: Number(val) } }))} sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ width: 36, textAlign: 'right' }}>{designs[selectedType]?.backgroundPositionY || 50}%</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="caption">Zoom</Typography>
                        <Slider value={designs[selectedType]?.backgroundSize || 100} min={50} max={200} onChange={(e, val) => setDesigns(d => ({ ...d, [selectedType]: { ...d[selectedType], backgroundSize: Number(val) } }))} sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ width: 36, textAlign: 'right' }}>{designs[selectedType]?.backgroundSize || 100}%</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Paper>
            {designs[selectedType]?.backgroundImage && (
              <Typography variant="caption" color="success.main" sx={{ mb: 2, display: 'block' }}>
                ✓ Image uploaded and saved
              </Typography>
            )}
          </Box>
          <Box sx={{ minWidth: 320, flex: '1 1 320px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Live Preview</Typography>
            <TicketPreview design={designs[selectedType] || {}} ticket={previewTicket} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!(designs[selectedType]?.heading && designs[selectedType]?.badgeColor)}>Save Design</Button>
      </DialogActions>
      <NotificationSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Dialog>
  );
}
